import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  AppState,
  gravatarsSelectors,
  GravatarThunks,
  SearchMetaRequestActions,
} from '@fullstory/redux';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { EventSearch } from '@fullstory/proto/fs/api/search';
import { useUserSessions } from './useUserSession';
import { useUserSessionData } from './useUserSessionData';
import { useUserMetadata } from './useUserMetadata';
import { useUserSessionConfig } from './useUserSessionConfig';
import { useUserClips } from './useUserClips';
import { UserDetailsResponse } from '@fullstory/client-api';
import { SegmentedControlOptionType } from '@fullstory/components';
import { SessionListItem, ClipListItem } from '../types';
import { useFlags } from '@fullstory/react-hooks';

const { getGravatars } = gravatarsSelectors;
type result = {
  userDetails: UserDetailsResponse | null;
  userMetadata: { [key: string]: string };
  sessionFilter: SegmentedControlOptionType | null;
  setSessionFilter: React.Dispatch<React.SetStateAction<SegmentedControlOptionType | null>>;
  sessions: SessionListItem[];
  clips: ClipListItem[];
  segmentedOptions: SegmentedControlOptionType[];
  loading: boolean;
  error: boolean;
  gravatarUrl: string;
};

export const useUserOverlay = (
  userId: string,
  orgId: string,
  currentSegment: SegmentDefinition,
  currentEventSearch?: EventSearch,
): result => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    segmentDefinition,
    eventSearch,
    segmentedOptions,
    sessionFilter,
    setSessionFilter,
    userDetails,
    matchingSessionsOrClips,
  } = useUserSessionConfig({ currentSegment, currentEventSearch });
  const { sessionData, allSessions } = useUserSessionData({
    segmentDefinition,
    eventSearch,
    sessionFilter,
    userId,
  });
  const { userMetadata, watchHistory } = useUserMetadata({ userDetails });
  const clips = useUserClips({ orgId, segmentDefinition, sessionData, watchHistory });
  const sessions = useUserSessions({
    orgId,
    segmentDefinition,
    sessionData,
    sessionFilter,
    userId,
    watchHistory,
  });
  const gravatarUrl = useSelector(
    (state: AppState) => getGravatars(state)[userDetails?.UserEmail ?? ''] ?? '',
  );

  useEffect(() => {
    dispatch(SearchMetaRequestActions.getHiddenVars(orgId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const flags = useFlags();
  useEffect(() => {
    if (!flags.featureFlags().AppGravatarProfiles) return;
    if (userDetails?.UserEmail) dispatch(GravatarThunks.fetchGravatars([userDetails.UserEmail]));
  }, [dispatch, userDetails?.UserEmail, flags]);

  return {
    userDetails,
    userMetadata,
    sessionFilter,
    setSessionFilter,
    sessions,
    clips,
    segmentedOptions,
    loading: allSessions.loading || matchingSessionsOrClips.loading,
    error: allSessions.error || matchingSessionsOrClips.error,
    gravatarUrl,
  };
};
